import { useSyncExternalStore } from 'react'

/**
 * Shared hero + header playground config for the umbrella homepage (`/`).
 *
 * The hero (UmbrellaHome) and the global header (UmbrellaHeader) live in
 * different parts of the tree, so this tiny external store keeps them in
 * sync: the selected background, the hero layout (rounded inset panel vs
 * full-bleed), and how the header behaves once it sticks. The on-page
 * configurator this was built for has been removed; 'flow' (Blue flow) is
 * the shipped default.
 */

export type HeroBg = {
  id: string
  label: string
  /** Governs contrasting text/logo colors over this background. */
  theme: 'dark' | 'light'
  image: string
  /** Optional wash over the image so the copy stays readable. */
  scrim?: string
}

export const HERO_BGS: HeroBg[] = [
  {
    id: 'curtains',
    label: 'Blue curtains',
    theme: 'dark',
    image: '/hero-curtains.jpg',
    scrim: 'linear-gradient(180deg, rgba(10,16,40,0.38) 0%, rgba(10,16,40,0.56) 100%)',
  },
  { id: 'indigo', label: 'Indigo waves', theme: 'dark', image: '/heroes/hero-indigo.jpg', scrim: 'rgba(0,0,0,0.15)' },
  { id: 'stripes', label: 'Violet stripes', theme: 'dark', image: '/heroes/hero-stripes.jpg', scrim: 'rgba(12,6,36,0.32)' },
  { id: 'stripes-deep', label: 'Deep stripes', theme: 'dark', image: '/heroes/hero-stripes-blue.jpg', scrim: 'rgba(10,5,30,0.22)' },
  { id: 'aurora', label: 'Aurora', theme: 'dark', image: '/heroes/hero-aurora.jpg', scrim: 'rgba(20,10,50,0.38)' },
  { id: 'glitch', label: 'Glitch', theme: 'dark', image: '/heroes/hero-glitch.jpg', scrim: 'rgba(8,12,45,0.42)' },
  { id: 'waves-white', label: 'White waves', theme: 'light', image: '/heroes/hero-waves-white.jpg', scrim: 'rgba(255,255,255,0.28)' },
  { id: 'dusk', label: 'Dusk', theme: 'dark', image: '/heroes/hero-dusk.jpg', scrim: 'linear-gradient(180deg, rgba(15,20,45,0.42) 0%, rgba(15,20,45,0.22) 100%)' },
  { id: 'soft', label: 'Soft light', theme: 'light', image: '/heroes/hero-soft.jpg', scrim: 'rgba(255,255,255,0.12)' },
  { id: 'ember', label: 'Ember', theme: 'dark', image: '/heroes/hero-ember.jpg', scrim: 'rgba(10,10,20,0.32)' },
  { id: 'frost', label: 'Frost', theme: 'light', image: '/heroes/hero-frost.jpg', scrim: 'rgba(255,255,255,0.18)' },
  { id: 'grid', label: 'Neon grid', theme: 'dark', image: '/heroes/hero-grid.jpg', scrim: 'rgba(8,10,30,0.15)' },
  { id: 'flow', label: 'Blue flow', theme: 'dark', image: '/heroes/hero-flow.jpg', scrim: 'rgba(20,12,50,0.30)' },
]

/** Rounded panel sitting below the header, or a full-bleed hero the header floats over. */
export type HeroLayout = 'inset' | 'fullbleed'
/** What the header turns into once it sticks on scroll (full-bleed only). */
export type HeaderScroll = 'white' | 'navy'

export type HeroConfig = {
  bgId: string
  layout: HeroLayout
  headerScroll: HeaderScroll
}

let state: HeroConfig = { bgId: 'flow', layout: 'inset', headerScroll: 'white' }
const listeners = new Set<() => void>()

export function setHeroConfig(patch: Partial<HeroConfig>): void {
  state = { ...state, ...patch }
  listeners.forEach((l) => l())
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function getSnapshot(): HeroConfig {
  return state
}

export function useHeroConfig(): HeroConfig {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function bgById(id: string): HeroBg {
  return HERO_BGS.find((b) => b.id === id) ?? HERO_BGS[0]
}
